import React from 'react';
import { connect } from 'react-redux';
import { Form, Input, InputNumber, Button, DatePicker, message, Icon } from 'antd';
import * as HttpStatus from 'http-status-codes';
import moment from 'moment';
import { createPromoCode } from '../../../../Broker/BrokerActions';
import {
  availableKeysSelector,
  brokerageSummarySelector,
  loadingBrokerSelector,
  loadingBrokerageSubscriptionsSelector,
} from '../../../../Broker/BrokerSelectors';
import { MSG_ERROR } from '../../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../../utils/httpError';

const FormItem = Form.Item;

const defaultExpiryDate = moment().add(1, 'year');

function resolveAvailableKeyCount(brokerageSummary, availableKeys) {
  const fromSummary = brokerageSummary?.remaining ?? brokerageSummary?.unAssigned;
  if (fromSummary != null) {
    return Math.max(0, Number(fromSummary) || 0);
  }
  return Array.isArray(availableKeys) ? availableKeys.length : 0;
}

const CreatePromoCodeForm = ({
  form,
  loading,
  createPromoCode,
  close,
  brokerageSummary,
  availableKeys,
}) => {
  const { getFieldDecorator } = form;
  const availableKeyCount = resolveAvailableKeyCount(brokerageSummary, availableKeys);

  const submit = e => {
    e.preventDefault();
    form.validateFields(async (err, fieldsValue) => {
      if (!err) {
        const values = Object.keys(fieldsValue).reduce((acc, key) => {
          if (key === 'expiryDate' && fieldsValue[key]) {
            acc.expiryDate = fieldsValue[key].toISOString();
            return acc;
          }
          acc[key] =
            fieldsValue[key] && typeof fieldsValue[key] === 'string' ? fieldsValue[key].trim() : fieldsValue[key];
          return acc;
        }, {});
        try {
          await createPromoCode(values);
          message.success(`The promo code '${values.code}' has been successfully created`);
          close();
        } catch (err) {
          if (err && err.status === HttpStatus.CONFLICT) {
            message.error(getApiErrorMessage(err, MSG_ERROR));
          } else {
            message.error(getApiErrorMessage(err, MSG_ERROR));
          }
        }
      }
    });
  };

  return (
    <Form layout="vertical" className="create-promo-code-form">
      <div className="create-promo-code-form__summary" aria-live="polite">
        <div className="create-promo-code-form__summary-icon" aria-hidden>
          <Icon type="key" />
        </div>
        <div className="create-promo-code-form__summary-body">
          <div className="create-promo-code-form__summary-label">Available subscription keys</div>
          <div className="create-promo-code-form__summary-value">{availableKeyCount}</div>
          <div className="create-promo-code-form__summary-hint">
            {availableKeyCount > 0
              ? `You can assign up to ${availableKeyCount} keys to this promo code.`
              : 'No unassigned keys available.'}
          </div>
        </div>
      </div>
      <FormItem label="Quantity">
        {getFieldDecorator('quantity', {
          initialValue: availableKeyCount > 0 ? 1 : undefined,
          rules: [
            { required: true, message: 'Please enter the quantity.' },
            {
              validator: (_, value, callback) => {
                if (value == null || value === '') {
                  callback();
                  return;
                }
                const quantity = Number(value);
                if (!Number.isInteger(quantity) || quantity < 1) {
                  callback('Enter a whole number of at least 1.');
                  return;
                }
                if (quantity > availableKeyCount) {
                  callback(`Quantity cannot exceed ${availableKeyCount} available keys.`);
                  return;
                }
                callback();
              },
            },
          ],
        })(
          <InputNumber
            min={1}
            max={availableKeyCount || 1}
            precision={0}
            step={1}
            placeholder="Quantity"
            disabled={loading || availableKeyCount === 0}
            style={{ width: '100%' }}
          />,
        )}
      </FormItem>
      <FormItem label="Code">
        {getFieldDecorator('code', {
          rules: [
            { required: true, message: 'Please input the code.' },
            { whitespace: true, message: 'Please input the code.' }
          ]
        })(<Input placeholder="Code" disabled={loading} spellCheck={false} />)}
      </FormItem>
      <FormItem label="Expiry Date" extra="Defaults to one year from today.">
        {getFieldDecorator('expiryDate', {
          initialValue: defaultExpiryDate,
          rules: [{ required: true, message: 'Please select an expiry date.' }]
        })(<DatePicker disabled={loading} style={{ width: '100%' }} format="DD/MM/YYYY" />)}
      </FormItem>
      <div className="create-promo-code-form__actions">
        <Button onClick={close} disabled={loading}>
          Cancel
        </Button>
        <Button type="primary" loading={loading} onClick={submit} disabled={availableKeyCount === 0}>
          {loading ? 'Saving' : 'Create'}
        </Button>
      </div>
    </Form>
  );
};

const mapStateToProps = state => ({
  loading: loadingBrokerageSubscriptionsSelector(state) || loadingBrokerSelector(state),
  brokerageSummary: brokerageSummarySelector(state),
  availableKeys: availableKeysSelector(state),
});

const mapDispatchToProps = { createPromoCode };

export default connect(mapStateToProps, mapDispatchToProps)(Form.create()(CreatePromoCodeForm));
